import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchOverview, fetchSites } from "../../redux/Slice/siteSlice";
import { money } from "../../utils/format";
import Stat from "../../components/Stat";
import PayModal from "./PayModal";
import SiteTable, { deleteDate } from "./SiteTable";
import useSiteActions from "./useSiteActions";
import Loader from "../../components/Loader";
import PartnerCard from "./PartnerCard";
import OrderQuickLinks from "./OrderQuickLinks";
import { UsageCard } from "../../components/UsageLimits";
import { getStoresWithoutProducts } from "../../services/admin";

// Dashboard home: website counts, expiring plans and shortcuts; what it shows depends on the role
export default function OverviewTab({ user, notify, setTab }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { overviewStats: stats, overviewError, sites, loading, error } = useSelector((state) => state.site);
  const isSuper = user.role === "superadmin";
  const isOwner = user.role === "storeadmin";

  const load = useCallback(() => {
    dispatch(fetchOverview());
    dispatch(fetchSites({ limit: 5, sort: "expiry" }));
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const actions = useSiteActions({ notify, reload: load });

  // Store owners: their websites with no product of their own yet. The "Add your products" card shows for them and
  // goes away once the owner has added a product.
  const [needProducts, setNeedProducts] = useState([]);
  useEffect(() => {
    if (!isOwner) return;
    getStoresWithoutProducts().then(({ data }) => setNeedProducts(data.stores || [])).catch(() => setNeedProducts([]));
  }, [isOwner]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const firstName = (user.name || "").split(" ")[0];
  const today = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).toUpperCase();

  return (
    <>
      {/* Store owners whose website has no product of their own yet: the animated "Add your products" card, first */}
      {isOwner && needProducts.map((site) => (
        <section className="add-products-cta" key={site.id}>
          <span className="add-products-cta-icon" aria-hidden="true">🛍️</span>
          <div className="add-products-cta-text">
            <b>Add your first product to {site.name}</b>
            <span>Your store is ready. Add your products with photos and prices, and start selling.</span>
          </div>
          <button type="button" className="add-products-btn" onClick={() => navigate(`/admin/sites/${site.id}/edit?tab=Products&add=1`)}>
            <span aria-hidden="true">＋</span> Add products
          </button>
        </section>
      ))}
      <div className="page-heading">
        <div>
          <p className="eyebrow">{today}</p>
          <h1>{isSuper ? `${greeting}, ${firstName}` : isOwner ? `Welcome back, ${firstName}` : "Your franchise overview"}</h1>
          <p className="subheading">Here’s what’s happening across your {isOwner ? "store" : "storefronts"} today.</p>
        </div>
        {!isOwner && (
          <button className="primary-btn" onClick={() => setTab(isSuper ? "All websites" : "Bulk purchase")}>
            ＋ {isSuper ? "Manage websites" : "Buy websites"}
          </button>
        )}
      </div>

      {/* Order management first: the quickest way into a store's orders (store owners have their own tab for it) */}
      {isOwner ? (
        <section className="panel order-links tab-gap">
          <div className="panel-heading">
            <div><h2>Order management</h2><p>See, confirm and ship your orders.</p></div>
            <button className="primary-btn" onClick={() => setTab("Order management")}>📦 Order management</button>
          </div>
        </section>
      ) : <OrderQuickLinks user={user} />}
      {/* Store owners: storage and email limits with add-ons */}
      {isOwner && <UsageCard />}

      {overviewError && <div className="admin-error">{overviewError}</div>}
      {/* Super Admin: alert if any domain linking is pending */}
      {isSuper && stats && stats.pendingDomainLinking > 0 && (
        <div className="domain-linking-banner" role="alert">
          <div className="domain-linking-banner-info">
            <span className="domain-linking-banner-icon">🌐</span>
            <div>
              <b>{stats.pendingDomainLinking} domain linking request{stats.pendingDomainLinking === 1 ? "" : "s"} pending:</b>{" "}
              {stats.pendingDomainStores && stats.pendingDomainStores.length > 0 ? (
                <span>
                  {stats.pendingDomainStores.slice(0, 3).map((s) => `${s.name} (${s.subdomain} → ${s.requestedDomain})`).join(", ")}
                  {stats.pendingDomainStores.length > 3 ? ` and ${stats.pendingDomainStores.length - 3} more` : ""}
                </span>
              ) : (
                "Subdomain stores need linking to their requested main domain."
              )}
            </div>
          </div>
          <button
            className="primary-btn domain-linking-banner-btn"
            onClick={() => {
              try { sessionStorage.setItem("oz_sites_filter", "Domain needs linking"); } catch {}
              setTab("All websites");
            }}
          >
            Review & Link Domains ({stats.pendingDomainLinking}) →
          </button>
        </div>
      )}
      {/* Store owners: a free trial ending soon (or ended) is deleted 7 days after it ends unless it is upgraded */}
      {isOwner && sites.filter((s) => s.deletesAt && s.daysRemaining <= 5).map((s) => (
        <div className="trial-delete-warning" key={s.id} role="alert">
          <b>{s.name}: your free trial {s.daysRemaining < 0 ? "has ended" : s.daysRemaining === 0 ? "ends today" : `ends in ${s.daysRemaining} day${s.daysRemaining === 1 ? "" : "s"}`}.</b>{" "}
          If you don't choose a paid plan, the website and all its products, orders and photos are deleted on <b>{deleteDate(s)}</b>.
          <button className="primary-btn" onClick={() => actions.openPay(s)}>Choose a plan</button>
        </div>
      ))}
      {!stats && !overviewError && <Loader label="Loading your dashboard" />}
      {stats && (
        <>
          {/* Both rows of numbers, highlighted together by the store owner tutorial */}
          <div className="overview-stats">
            <div className="stats-grid">
              <Stat label={isOwner ? "My websites" : "Total websites"} value={stats.totalWebsites} meta="Across all storefronts" icon="▤" />
              <Stat label="Active websites" value={stats.activeWebsites} meta="Currently accessible" icon="↗" />
              <Stat label="Paid subscriptions" value={stats.paidSubscriptions} meta="Active paid plans" icon="₹" />
              <Stat label="Expiring soon" value={stats.expiringSoon} meta="Due within 5 days" icon="◷" warn={stats.expiringSoon > 0} />
            </div>
            {/* Store owners: their orders across all their websites */}
            {isOwner && stats.orders && (
              <div className="stats-grid">
                <Stat label="Total orders" value={stats.orders.all} meta={`Worth ${money(stats.orders.revenue)}`} icon="🧾" />
                <Stat label="Pending orders" value={stats.orders.pending} meta="Confirmed, processing or packed" icon="⏳" warn={stats.orders.pending > 0} />
                <Stat label="In transit" value={stats.orders.shipped} meta="Shipped / out for delivery" icon="🚚" />
                <Stat label="Delivered" value={stats.orders.delivered} meta={`${stats.orders.cancelled} cancelled or refunded`} icon="✅" />
              </div>
            )}
          </div>
          {(isSuper || user.role === "subadmin") && (
            <div className="stats-grid">
              <Stat label="Free trials" value={stats.trialWebsites} meta="Trial websites running" icon="◉" />
              <Stat label="Expired" value={stats.expiredWebsites} meta="Need a payment to restart" icon="◷" warn={stats.expiredWebsites > 0} />
              <Stat label="Monthly recurring" value={money(stats.monthlyRecurringRevenue)} meta="From paid websites" icon="₹" />
              {isSuper ? (
                stats.pendingDomainLinking > 0 ? (
                  <Stat
                    label="Needs domain linking"
                    value={stats.pendingDomainLinking}
                    meta="Main domains to link"
                    icon="🌐"
                    warn
                  />
                ) : (
                  <Stat label="Franchise partners" value={stats.franchiseCount} meta={`${stats.allocatedWebsites} websites allocated`} icon="♧" />
                )
              ) : (
                <Stat label="Licences left" value={stats.licencesRemaining} meta="Websites you can still create" icon="▦" />
              )}
            </div>
          )}
          {user.role === "subadmin" && <PartnerCard stats={stats} notify={notify} />}
        </>
      )}

      <section className="panel sites-panel">
          <div className="panel-heading">
            <div>
              <h2>{isSuper ? "Website subscriptions" : "Your websites"}</h2>
              <p>Soonest to expire first.</p>
            </div>
            <button className="text-btn" onClick={() => setTab(isSuper ? "All websites" : "My websites")}>View all →</button>
          </div>
          {error && <div className="admin-error">{error}</div>}
          <SiteTable sites={sites} user={user} loading={loading} onToggle={actions.toggle} onPay={actions.openPay} onLinkDomain={actions.linkDomain} />
      </section>

      {actions.payTarget && <PayModal site={actions.payTarget} isSuperAdmin={isSuper} onSubmit={actions.pay} onClose={actions.closePay} />}
    </>
  );
}
